import { cn } from '@/lib/utils';
import { parseOccSymbol, formatExpirationDate, getDaysToExpiration, formatCurrency } from '@/lib/formatters';

export interface OptionSymbolProps {
  symbol: string;
  showDte?: boolean;
  compact?: boolean;
  className?: string;
}

export function OptionSymbol({ symbol, showDte = false, compact = false, className }: OptionSymbolProps) {
  const parsed = parseOccSymbol(symbol);

  if (!parsed) {
    return <span className={cn('font-mono text-sm', className)}>{symbol}</span>;
  }

  const { underlying, expiration, type, strike } = parsed;
  const dte = getDaysToExpiration(expiration);
  const isExpired = dte < 0;
  const isNearExpiration = dte >= 0 && dte <= 7;

  const strikeStr = strike % 1 === 0 ? strike.toString() : strike.toFixed(2);
  const typeLabel = type === 'call' ? 'C' : 'P';
  const typeBgClass = type === 'call' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700';

  if (compact) {
    return (
      <span className={cn('font-mono text-sm', className)}>
        {underlying} {formatExpirationDate(expiration)} ${strikeStr}
        <span className={cn('ml-1 px-1 rounded text-xs', typeBgClass)}>{typeLabel}</span>
      </span>
    );
  }

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <span className="font-semibold text-gray-900">{underlying}</span>
      <span className={cn('px-1.5 py-0.5 rounded text-xs font-medium', typeBgClass)}>
        {type === 'call' ? 'Call' : 'Put'}
      </span>
      <span className="text-gray-600">${strikeStr}</span>
      <span
        className={cn(
          'text-sm',
          isExpired ? 'text-red-600' : isNearExpiration ? 'text-yellow-600' : 'text-gray-500'
        )}
      >
        {formatExpirationDate(expiration)}
        {showDte && !isExpired && (
          <span className="ml-1 text-xs">({dte}d)</span>
        )}
        {isExpired && <span className="ml-1 text-xs">(Expired)</span>}
      </span>
    </div>
  );
}

export interface OptionLegDisplayProps {
  action: 'BTO' | 'STO' | 'BTC' | 'STC';
  symbol: string;
  quantity: number;
  price: number;
  className?: string;
}

export function OptionLegDisplay({ action, symbol, quantity, price, className }: OptionLegDisplayProps) {
  const isSell = action === 'STO' || action === 'STC';
  const actionColor = isSell ? 'text-green-600' : 'text-red-600';

  return (
    <div className={cn('flex items-center justify-between py-2', className)}>
      <div className="flex items-center gap-3">
        <span className={cn('font-mono text-xs font-medium w-8', actionColor)}>{action}</span>
        <span className="text-gray-700">{quantity}x</span>
        <OptionSymbol symbol={symbol} compact />
      </div>
      <span className={cn('font-mono', actionColor)}>
        {isSell ? '+' : '-'}{formatCurrency(price)}
      </span>
    </div>
  );
}

/**
 * Leg data that can come from parsed symbol or direct data
 */
export interface LegData {
  action: 'BTO' | 'STO' | 'BTC' | 'STC';
  optionType: 'call' | 'put';
  strikePrice: number;
  expirationDate: string | Date;
  quantity: number;
  pricePerContract: number;
  // Optional close reason for closed legs
  closeReason?: 'expired' | 'assigned' | 'exercised' | 'closed' | null;
  // Optional Greeks (available when fetched from market data)
  delta?: number;
  theta?: number;
  vega?: number;
  impliedVolatility?: number;
}

interface LegTableProps {
  legs: LegData[];
  className?: string;
}

function getLegCloseReasonBadge(reason: string | null | undefined): { label: string; className: string } | null {
  if (!reason) return null;
  switch (reason) {
    case 'expired': return { label: 'Expired', className: 'bg-gray-100 text-gray-600' };
    case 'assigned': return { label: 'Assigned', className: 'bg-amber-100 text-amber-700' };
    case 'exercised': return { label: 'Exercised', className: 'bg-purple-100 text-purple-700' };
    case 'closed': return { label: 'Closed', className: 'bg-green-100 text-green-700' };
    default: return null;
  }
}

/**
 * Table-style display of option legs with columns
 */
export function LegTable({ legs, className }: LegTableProps) {
  // Check if any leg has Greeks data
  const hasGreeks = legs.some(leg => leg.delta !== undefined);
  // Check if any leg has close reason
  const hasCloseReason = legs.some(leg => leg.closeReason);

  return (
    <div className={cn('overflow-x-auto', className)}>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-gray-500 border-b border-gray-200">
            <th className="py-2 pr-3 font-medium">Type</th>
            <th className="py-2 pr-3 font-medium">Side</th>
            <th className="py-2 pr-3 font-medium text-right">Strike</th>
            <th className="py-2 pr-3 font-medium">Expiration</th>
            <th className="py-2 pr-3 font-medium text-right">DTE</th>
            <th className="py-2 pr-3 font-medium text-right">Qty</th>
            {hasGreeks && (
              <>
                <th className="py-2 pr-3 font-medium text-right">Delta</th>
                <th className="py-2 pr-3 font-medium text-right">Theta</th>
                <th className="py-2 pr-3 font-medium text-right">IV</th>
              </>
            )}
            <th className="py-2 pr-3 font-medium text-right">Price</th>
            {hasCloseReason && <th className="py-2 font-medium">Outcome</th>}
          </tr>
        </thead>
        <tbody>
          {legs.map((leg, index) => {
            const isSell = leg.action === 'STO' || leg.action === 'STC';
            const isPut = leg.optionType === 'put';
            const expDate = leg.expirationDate instanceof Date
              ? leg.expirationDate
              : new Date(leg.expirationDate);
            const totalValue = leg.pricePerContract * leg.quantity * 100;
            const dte = getDaysToExpiration(expDate);
            const isExpired = dte < 0;
            const isNearExpiration = dte >= 0 && dte <= 7;

            return (
              <tr key={index} className="border-b border-gray-100 last:border-0">
                <td className="py-2 pr-3">
                  <span className={cn(
                    'inline-block px-1.5 py-0.5 rounded text-xs font-medium',
                    isPut ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                  )}>
                    {isPut ? 'PUT' : 'CALL'}
                  </span>
                </td>
                <td className="py-2 pr-3">
                  <span className={cn(
                    'font-medium',
                    isSell ? 'text-green-600' : 'text-red-600'
                  )}>
                    {isSell ? 'SELL' : 'BUY'}
                  </span>
                </td>
                <td className="py-2 pr-3 text-right font-mono">
                  ${leg.strikePrice % 1 === 0 ? leg.strikePrice : leg.strikePrice.toFixed(2)}
                </td>
                <td className="py-2 pr-3 text-gray-600">
                  {formatExpirationDate(expDate)}
                </td>
                <td className={cn(
                  'py-2 pr-3 text-right font-mono',
                  isExpired ? 'text-red-600' : isNearExpiration ? 'text-yellow-600' : 'text-gray-600'
                )}>
                  {isExpired ? 'Exp' : dte}
                </td>
                <td className="py-2 pr-3 text-right">
                  {leg.quantity}
                </td>
                {hasGreeks && (
                  <>
                    <td className="py-2 pr-3 text-right font-mono text-gray-700">
                      {leg.delta !== undefined ? leg.delta.toFixed(2) : '-'}
                    </td>
                    <td className="py-2 pr-3 text-right font-mono text-gray-700">
                      {leg.theta !== undefined ? formatCurrency(leg.theta) : '-'}
                    </td>
                    <td className="py-2 pr-3 text-right font-mono text-gray-700">
                      {leg.impliedVolatility !== undefined ? `${(leg.impliedVolatility * 100).toFixed(0)}%` : '-'}
                    </td>
                  </>
                )}
                <td className={cn(
                  'py-2 pr-3 text-right font-mono',
                  isSell ? 'text-green-600' : 'text-red-600'
                )}>
                  {isSell ? '+' : '-'}{formatCurrency(totalValue)}
                </td>
                {hasCloseReason && (
                  <td className="py-2">
                    {(() => {
                      const badge = getLegCloseReasonBadge(leg.closeReason);
                      if (!badge) return <span className="text-gray-400">-</span>;
                      return (
                        <span className={cn('px-1.5 py-0.5 rounded text-xs font-medium', badge.className)}>
                          {badge.label}
                        </span>
                      );
                    })()}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
